import type { ProfileData } from "../data/profiles";

const W = 1080;
const H = 1350;

function wrapText(
  ctx: CanvasRenderingContext2D,
  text: string,
  maxWidth: number
): string[] {
  const words = text.split(/\s+/);
  const lines: string[] = [];
  let line = "";
  for (const w of words) {
    const test = line ? `${line} ${w}` : w;
    if (ctx.measureText(test).width > maxWidth && line) {
      lines.push(line);
      line = w;
    } else {
      line = test;
    }
  }
  if (line) lines.push(line);
  return lines;
}

function drawHex(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  r: number
) {
  ctx.beginPath();
  for (let i = 0; i < 6; i++) {
    const a = (Math.PI / 3) * i - Math.PI / 2;
    const x = cx + r * Math.cos(a);
    const y = cy + r * Math.sin(a);
    if (i === 0) ctx.moveTo(x, y);
    else ctx.lineTo(x, y);
  }
  ctx.closePath();
}

function loadImage(src: string): Promise<HTMLImageElement | null> {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = src;
  });
}

export async function generateShareCardBlob(profile: ProfileData, percentage: number): Promise<Blob | null> {
  const canvas = document.createElement("canvas");
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext("2d");
  if (!ctx) return null;

  // Fond gradient-hero
  const grad = ctx.createLinearGradient(0, 0, W, H);
  grad.addColorStop(0, "hsl(222, 55%, 14%)");
  grad.addColorStop(0.5, "hsl(155, 55%, 22%)");
  grad.addColorStop(1, "hsl(160, 45%, 42%)");
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, W, H);

  // Décor hexagones
  ctx.lineWidth = 2;
  const hexes = [
    { x: 120, y: 140, r: 60, op: 0.07 },
    { x: 940, y: 200, r: 90, op: 0.08 },
    { x: 80, y: 720, r: 110, op: 0.06 },
    { x: 980, y: 900, r: 70, op: 0.09 },
    { x: 200, y: 1180, r: 90, op: 0.06 },
    { x: 880, y: 1240, r: 50, op: 0.08 },
  ];
  hexes.forEach((h) => {
    ctx.strokeStyle = `rgba(255,255,255,${h.op})`;
    drawHex(ctx, h.x, h.y, h.r);
    ctx.stroke();
  });

  // Logo officiel (PNG blanc)
  const logo = await loadImage("/logo-icon-white.png");
  if (logo) {
    const logoSize = 200;
    ctx.drawImage(logo, W / 2 - logoSize / 2, 100, logoSize, logoSize);
  }

  // Emoji profil — gros au centre
  ctx.font = "200px 'Apple Color Emoji', 'Segoe UI Emoji', 'Noto Color Emoji', sans-serif";
  ctx.textAlign = "center";
  ctx.textBaseline = "alphabetic";
  ctx.fillStyle = "#ffffff";
  ctx.fillText(profile.emoji, W / 2, 500);

  // "Mon profil :"
  ctx.font = "600 32px Comfortaa, sans-serif";
  ctx.fillStyle = "rgba(255,255,255,0.85)";
  ctx.fillText("Mon profil :", W / 2, 580);

  // Nom du profil
  ctx.font = "900 96px Jost, sans-serif";
  ctx.fillStyle = "#ffffff";
  ctx.fillText(profile.name, W / 2, 700);

  // X% match
  ctx.font = "700 48px Jost, sans-serif";
  ctx.fillStyle = "hsl(42, 88%, 62%)";
  ctx.fillText(`${percentage}% match`, W / 2, 770);

  // Tagline (wrap, italic)
  ctx.font = "italic 32px Comfortaa, sans-serif";
  ctx.fillStyle = "rgba(255,255,255,0.92)";
  const taglineLines = wrapText(ctx, profile.tagline, 800);
  let y = 870;
  taglineLines.forEach((line) => {
    ctx.fillText(line, W / 2, y);
    y += 44;
  });

  // Punchline (vert-eau)
  ctx.font = "700 36px Jost, sans-serif";
  ctx.fillStyle = "hsl(160, 40%, 78%)";
  const punchLines = wrapText(ctx, profile.punchline, 900);
  y += 30;
  punchLines.forEach((line) => {
    ctx.fillText(line, W / 2, y);
    y += 50;
  });

  // Watermark
  ctx.font = "900 28px Jost, sans-serif";
  ctx.fillStyle = "rgba(255,255,255,0.6)";
  ctx.fillText("OMMISTACK", W / 2, H - 80);
  ctx.font = "400 22px Comfortaa, sans-serif";
  ctx.fillText("quiz.ommistack.com", W / 2, H - 44);

  return await new Promise<Blob | null>((resolve) =>
    canvas.toBlob((b) => resolve(b), "image/png", 0.92)
  );
}
